import React, { useState } from 'react';
import { ChevronDownIcon, PackageSearchIcon, SlidersHorizontalIcon } from 'lucide-react';
import type { CategoryId } from '../../types/product';
import { FilterPanel } from './FilterPanel';
import { ActiveFilters } from './ActiveFilters';
import { MobileFilterDrawer } from './MobileFilterDrawer';
import { ProductCard } from '../product/ProductCard';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useProductFilters, type SortId } from '../../hooks/useProductFilters';
import { categories } from '../../data/categories';
import { sortOptions } from '../../data/filters';
export function ShopCatalog({
  category,
  deals
}: {
  category?: CategoryId;
  deals: boolean;
}) {
  const api = useProductFilters(category, deals);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const cat = categories.find(c => c.id === category);
  const title = deals ? 'Deals' : cat?.name ?? 'All products';
  const subtitle = deals ? 'Limited-time prices on batteries, power stations and panels.' : cat ? `${cat.description}. Free delivery and expert advice included.` : 'Batteries, power stations, solar panels and everything in between.';
  return <div className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6 lg:px-8" data-id="element-569" data-mp="1m69gdlv0ppd1">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, ...(cat || deals ? [{
      label: 'Shop',
      href: '/shop'
    }] : []), {
      label: title
    }]} data-id="element-570" data-mp="1m8dka3sx2odb" />

      <div className="mt-4 border-b border-line pb-6" data-id="element-571" data-mp="1ddfnumqk8th8">
        <h1 className={`text-[28px] font-bold tracking-tight sm:text-4xl ${deals ? 'text-sale' : 'text-ink'}`} data-id="element-572" data-mp="1dbuu2zc2lekf">{title}</h1>
        <p className="mt-2 max-w-xl text-sm text-muted" data-id="element-573" data-mp="13frv22cilw1p">{subtitle}</p>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[240px_1fr] lg:gap-10" data-id="element-574" data-mp="13dotqm5wwgho">
        <aside className="hidden lg:block" aria-label="Filters" data-id="element-575" data-mp="18evzygsep7m8">
          <div className="sticky top-40 max-h-[calc(100vh-11rem)] overflow-y-auto pb-6 pr-2" data-id="element-576" data-mp="ufwd9eogf2sp">
            <FilterPanel api={api} data-id="element-577" data-mp="ygoo2ccz5fr3" />
          </div>
        </aside>

        <div data-id="element-578" data-mp="3133riq4b444f">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-579" data-mp="313lwqc8y5soh">
            <div className="flex items-center gap-3" data-id="element-580" data-mp="2wcdldao23x8o">
              <button type="button" onClick={() => setDrawerOpen(true)} className="inline-flex h-10 items-center gap-2 rounded-lg border border-line px-3.5 text-sm font-semibold text-ink hover:border-ink/40 lg:hidden" data-id="element-581" data-mp="2wblsi7lugdm3">
                <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-582" data-mp="3k0wtnq73wa0r" />
                Filters
                {api.activeCount > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ink px-1.5 text-[11px] text-white" data-id="element-583" data-mp="3f1235nr8tmg8">{api.activeCount}</span>}
              </button>
              <p className="text-sm text-muted" aria-live="polite" data-id="element-584" data-mp="364jc6h2ko8ma">
                <span className="font-semibold text-ink" data-id="element-585" data-mp="1yq6hy43hoaxw">{api.results.length}</span> products
              </p>
            </div>
            <label className="relative flex items-center gap-2 text-sm text-muted" data-id="element-586" data-mp="1up4e19ymnapl">
              <span className="hidden sm:inline" data-id="element-587" data-mp="a6twflss5sq0">Sort by</span>
              <select value={api.sort} onChange={e => api.setSort(e.target.value as SortId)} className="h-10 appearance-none rounded-lg border border-line bg-white pl-3 pr-9 text-sm font-medium text-ink focus:border-ink focus:outline-none" data-id="element-588" data-mp="e5sx2u7hb3bn">
                {sortOptions.map(o => <option key={o.id} value={o.id} data-id="element-589" data-mp="sxtgq9pbyecy">
                    {o.label}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 h-4 w-4 text-muted" aria-hidden="true" data-id="element-590" data-mp="11x86f6xoakwe" />
            </label>
          </div>

          <div className="mt-4" data-id="element-591" data-mp="16svutk4bza0u">
            <ActiveFilters api={api} data-id="element-592" data-mp="33obhtyfe03i0" />
          </div>

          {api.results.length === 0 ? <div className="mt-6 flex flex-col items-center rounded-2xl bg-surface px-6 py-16 text-center" data-id="element-593" data-mp="38m3boxzldzou">
              <PackageSearchIcon className="h-10 w-10 text-ink/25" aria-hidden="true" data-id="element-594" data-mp="2dbn6bd47qakf" />
              <p className="mt-4 text-base font-semibold text-ink" data-id="element-595" data-mp="28dv8y3kstw8u">No products match these filters</p>
              <p className="mt-1 text-sm text-muted" data-id="element-596" data-mp="2navzw4v61n69">Try removing a filter or browse all products.</p>
              <button type="button" onClick={api.clearAll} className="mt-6 h-11 rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-597" data-mp="2ib9ntyr2qcz1">
                Clear all filters
              </button>
            </div> : <ul className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3 xl:gap-5" data-id="element-598" data-mp="1pkr9g1kwgp6b">
              {api.results.map(p => <li key={p.id} data-id="element-599" data-mp="1zlbdfbzt07s8">
                  <ProductCard product={p} data-id="element-600" data-mp="1zk099b2g4y0l" />
                </li>)}
            </ul>}
        </div>
      </div>

      <MobileFilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} api={api} data-id="element-601" data-mp="1btcq1tn2ylyq" />
    </div>;
}